import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

interface Post {
  id: number;
  title: { rendered: string };
  slug: string;
  date: string;
  categories: number[];
  author: number;
  _embedded?: any;
}

export default function LatestArticles() {
  const [posts, setPosts] = useState<Post[]>(() => {
    const cached = localStorage.getItem('latestPosts');
    return cached ? JSON.parse(cached) : [];
  });
  const [loading, setLoading] = useState(posts.length === 0);
  const [avatars, setAvatars] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const res = await fetch(
          'https://blog.thecryptotutor.com/wp-json/wp/v2/posts?per_page=4&orderby=date&order=desc&_embed'
        );
        const data = await res.json();
        setPosts(data);
        localStorage.setItem('latestPosts', JSON.stringify(data));

        const avatarMap: Record<number, string> = {};
        await Promise.all(
          data.map(async (post: Post) => {
            const author = post._embedded?.author?.[0];
            if (!author) return;

            if (author.acf?.custom_avatar) {
              try {
                const mediaRes = await fetch(
                  `https://blog.thecryptotutor.com/wp-json/wp/v2/media/${author.acf.custom_avatar}`
                );
                const mediaData = await mediaRes.json();
                avatarMap[author.id] = mediaData.source_url || '/default-avatar.jpg';
              } catch {
                avatarMap[author.id] = '/default-avatar.jpg';
              }
            } else if (author.avatar_urls?.['96']) {
              avatarMap[author.id] = author.avatar_urls['96'];
            } else {
              avatarMap[author.id] = '/default-avatar.jpg';
            }
          })
        );

        setAvatars(avatarMap);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatest();
  }, []);

  const skeletonCards = Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="animate-pulse flex flex-col h-80 bg-gray-200 rounded-lg" />
  ));

  return (
    <section className="py-5 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">The Latest Articles</h2>
            <Link to="/blog">
              <Button variant="outline" size="sm">
                View More <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading && posts.length === 0
              ? skeletonCards
              : posts.map(post => {
                  const featuredMedia =
                    post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/default-image.jpg';

                  const authorData = post._embedded?.author?.[0];
                  const authorAvatar =
                    authorData ? avatars[authorData.id] || '/default-avatar.jpg' : '/default-avatar.jpg';

                  const postDate = new Date(post.date);
                  const formattedDate = postDate.toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  });

                  const categoriesData =
                    post._embedded?.['wp:term']?.find((group: any) => group[0]?.taxonomy === 'category');

                  const categoryName =
                    categoriesData?.find((c: any) => post.categories.includes(c.id))?.name ||
                    'Uncategorized';

                  return (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="group h-full">
                      <Card hover className="flex flex-col h-full overflow-hidden">
                        <div className="aspect-video overflow-hidden flex-shrink-0">
                          <img
                            src={featuredMedia}
                            alt={post.title.rendered}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
<div className="p-4 flex flex-col flex-1">

  {/* Title at top */}
  <h3
    className="font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors"
    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
  ></h3>

  {/* Push this entire block to the bottom */}
  <div className="mt-auto">

    {/* Author + Date */}
    <div className="flex items-center justify-between text-xs text-gray-500 space-x-2">
      <div className="flex items-center space-x-2">
        <img
          src={authorAvatar}
          alt={authorData?.name || 'Author'}
          className="w-5 h-5 rounded-lg object-cover"
        />
        {authorData && (
          <Link
            to={`/author/${authorData.slug}`}
            className="hover:text-blue-600 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {authorData.acf?.full_name || authorData.name}
          </Link>
        )}
      </div>
      <span>{formattedDate}</span>
    </div>

    {/* Category */}
    <div className="mt-2 text-xs text-blue-600 font-medium truncate">
      {categoryName}
    </div>

  </div>
</div>

                      </Card>
                    </Link>
                  );
                })}
          </div>
        </div>
      </div>
    </section>
  );
}
